import { memo } from 'react'
import DataTable, { IDataTableColumn } from 'react-data-table-component'
import { GS, GS_m } from 'back-end-api'
import { useTranslation } from 'services/Translation'
import LoaderMini from 'components/common/loaders/mini'

import texts from '../texts'
import {
  TimeFilterValue,
  buildTimeFilterParams,
  isCustomRangeValid,
  productLabel,
  formatDuration,
} from '../helpers'
import { Panel, Notice, EmptyState } from '../Styles'

interface ServiceMetricsProps {
  filter: TimeFilterValue
}

const columns = (
  t: (key: string) => string,
  none: string
): IDataTableColumn<GS_m.AnalyticsServiceMetricsRow>[] => [
  {
    name: t('product'),
    cell: r => productLabel(r.product, t),
    minWidth: '160px',
  },
  {
    name: t('consultCount'),
    center: true,
    cell: r => r.consult_count ?? 0,
  },
  {
    name: t('avgDuration'),
    right: true,
    // Empty (retained) for MIA Basic / Total / MIA Complete with no consults
    // (Requirements 7.3 / 7.4).
    cell: r => formatDuration(r.average_duration_minutes, none),
  },
]

// Task 15.3 — Service Metrics tab (Requirements 7.1, 7.2).
// Per-product consult counts plus the average MIA Complete duration in minutes.
const ServiceMetrics = ({ filter }: ServiceMetricsProps) => {
  const { t } = useTranslation(texts)

  const rangeValid = isCustomRangeValid(filter)
  const params: GS_m.GetWalletServiceMetricsParams = buildTimeFilterParams(filter)

  const { data, error, isLoading } = GS.useWalletServiceMetrics(params, {
    enabled: rangeValid,
  })

  const rows = data?.rows ?? []

  if (!rangeValid) {
    return (
      <Panel>
        <EmptyState>{t('invalidRange')}</EmptyState>
      </Panel>
    )
  }

  return (
    <Panel>
      {error ? (
        <Notice variant="error" data-cy="serviceError">
          {t('errorLoading')}
        </Notice>
      ) : (
        <DataTable
          noHeader
          columns={columns(t, t('none'))}
          data={rows}
          progressPending={isLoading}
          progressComponent={<LoaderMini />}
          noDataComponent={<EmptyState>{t('empty')}</EmptyState>}
          dense
          highlightOnHover
        />
      )}
    </Panel>
  )
}

ServiceMetrics.whyDidYouRender = true

export default memo(ServiceMetrics)
